import React from "react";
import { Link } from "react-router-dom";
import { Globe, MessageSquare, ExternalLink, BookOpenCheck, AlertTriangle } from "lucide-react";
import {
  RK, ReportLabel, ReportList, ReportRichList, ReportChip,
} from "../reports/ReportKit";

function CompanyResearchCard({ research }) {
  if (!research) return null;
  return (
    <div
      className="rm-card-static p-6 md:p-8 mb-8"
      data-testid="research-card"
      style={{ background: "#FFFBEB" }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: "#0F1B2D", color: "white", border: "3px solid #0F1B2D" }}
        >
          <Globe size={20} strokeWidth={2.5} />
        </div>
        <div>
          <div className="rm-label">Company Research</div>
          <h2 className="font-display text-2xl uppercase leading-tight">Pre-interview brief.</h2>
        </div>
      </div>

      {research.snapshot && (
        <div className="mb-4">
          <ReportLabel color={RK.navy} className="mb-1">Snapshot</ReportLabel>
          <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{research.snapshot.what_they_do}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {research.snapshot.industry && <ReportChip label={research.snapshot.industry} tone="nice" />}
            {research.snapshot.size_estimate && (
              <ReportChip label={research.snapshot.size_estimate} tone="nice" />
            )}
            {research.snapshot.stage && <ReportChip label={research.snapshot.stage} tone="nice" />}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ReportList title="Talking points" color={RK.navy} items={research.talking_points} />
        <ReportList title="Likely priorities" color={RK.blue} items={research.likely_priorities} />
        <ReportList title="Signals to verify" color={RK.amber} items={research.recent_signals} />
        <ReportList title="Ask the interviewer" color={RK.purple} items={research.questions_to_ask} />
        <ReportList title="Probe these" icon={<AlertTriangle size={11} />} color={RK.red} items={research.red_flags_to_probe} />
        <ReportList title="Resume tweaks based on research" color={RK.green} items={research.tailoring_tips} />
      </div>

      {(research.research_links || []).length > 0 && (
        <div className="mt-5">
          <div className="rm-label mb-2">Open these next</div>
          <div className="flex flex-wrap gap-2">
            {research.research_links.map((l, i) => (
              <a
                key={i}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rm-tag flex items-center gap-1.5 hover:bg-yellow-100"
                data-testid={`research-link-${i}`}
              >
                {l.label} <ExternalLink size={12} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InterviewPrepCard({ prep }) {
  if (!prep) return null;
  return (
    <div
      className="rm-card-static p-6 md:p-8 mb-8"
      data-testid="interview-prep-card"
      style={{ background: "#0F1B2D", color: "white" }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: "#FFC145", color: "#0F1B2D", border: "3px solid #FFC145" }}
        >
          <MessageSquare size={20} strokeWidth={2.5} />
        </div>
        <div>
          <div className="rm-label" style={{ color: "#FFC145" }}>
            Interview Prep
          </div>
          <h2 className="font-display text-2xl uppercase leading-tight">Tomorrow's playbook.</h2>
        </div>
      </div>

      {prep.elevator_pitch && (
        <div
          className="mb-5 p-4 rounded-md"
          style={{ background: "white", color: "#0F1B2D", border: "1.5px solid #FFC145" }}
        >
          <div className="rm-label mb-1">60-second pitch</div>
          <p className="text-sm">{prep.elevator_pitch}</p>
        </div>
      )}

      {(prep.likely_questions || []).length > 0 && (
        <div className="space-y-3">
          <div className="rm-label" style={{ color: "#FFC145" }}>
            Likely questions
          </div>
          {prep.likely_questions.map((q, i) => (
            <details
              key={i}
              className="p-4 rounded-md"
              style={{ background: "#1E293B", border: "1px solid rgba(255,193,69,0.45)" }}
              data-testid={`interview-question-${i}`}
            >
              <summary className="cursor-pointer font-bold text-sm flex items-start justify-between gap-2">
                <span>{q.question}</span>
                {q.category && (
                  <span
                    className="rm-tag flex-shrink-0"
                    style={{
                      background: "#FFC145",
                      borderColor: "#FFC145",
                      color: "#0F1B2D",
                    }}
                  >
                    {q.category}
                  </span>
                )}
              </summary>
              {q.why_asked && (
                <div className="text-xs mt-2 italic" style={{ color: "#94A3B8" }}>
                  Why asked: {q.why_asked}
                </div>
              )}
              {q.answer_scaffold && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {Object.entries(q.answer_scaffold).map(
                    ([k, v]) =>
                      v && (
                        <div key={k} className="p-2 rounded" style={{ background: "#0F1B2D" }}>
                          <div className="rm-label mb-1" style={{ color: "#FFC145" }}>
                            {k}
                          </div>
                          <div>{v}</div>
                        </div>
                      ),
                  )}
                </div>
              )}
            </details>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        {(prep.questions_for_interviewer || []).length > 0 && (
          <div className="p-4 rounded-md" style={{ background: "white", color: "#0F1B2D" }}>
            <ReportList title="Ask them" color={RK.purple} items={prep.questions_for_interviewer} />
          </div>
        )}
        {(prep.gap_mitigation || []).length > 0 && (
          <div className="p-4 rounded-md" style={{ background: "white", color: "#0F1B2D" }}>
            <ReportRichList
              title="Address your gaps"
              color={RK.red}
              items={prep.gap_mitigation.map((g) => ({ heading: g.gap, body: g.talking_track }))}
            />
          </div>
        )}
      </div>

      {prep.closing_statement && (
        <div className="mt-5 p-4 rounded-md" style={{ background: "#FFC145", color: "#0F1B2D" }}>
          <div className="rm-label mb-1">Closing statement</div>
          <p className="text-sm">{prep.closing_statement}</p>
        </div>
      )}

      {(prep.logistics_checklist || []).length > 0 && (
        <div className="mt-5">
          <div className="rm-label mb-2" style={{ color: "#FFC145" }}>
            Day-of checklist
          </div>
          <ul className="text-sm space-y-1 font-mono-rm">
            {prep.logistics_checklist.map((c, i) => (
              <li key={i}>☐ {c}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ResearchInterviewTab({ research, prep, analysisId }) {
  return (
    <>
      <CompanyResearchCard research={research} />
      <InterviewPrepCard prep={prep} />
      {analysisId && (
        <div
          className="rm-card-static p-5 mb-8 flex items-center gap-3 flex-wrap"
          style={{ background: "#FFFBEB", borderColor: "#FFC145", boxShadow: "5px 5px 0 0 #FF6A00" }}
          data-testid="dashboard-build-playbook-cta"
        >
          <BookOpenCheck size={20} style={{ color: "#FF6A00" }} className="flex-shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="rm-label">Take it further</div>
            <div className="text-sm">
              Turn this into a one-page <strong>Interview Playbook</strong> — likely questions
              + evidence pulled from your resume + STAR-framed answers.
            </div>
          </div>
          <Link
            to={`/playbooks?analysis_id=${analysisId}`}
            className="rm-btn rm-btn-primary text-xs px-3 py-2 flex-shrink-0"
            data-testid="open-playbook-from-dashboard"
          >
            Build Playbook
          </Link>
        </div>
      )}
    </>
  );
}
